import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { useStickerCanvas, type StickerCanvasTool } from '../../features/sticker-editor/useStickerCanvas';
import { API_BASE, api, logHttpError } from '../../services/api';

export interface EditableSticker {
  id: number;
  image_url: string;
}

interface StickerEditorModalProps {
  sticker: EditableSticker;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const tools: Array<{ id: StickerCanvasTool; label: string; icon: string }> = [
  { id: 'background', label: 'Удалить фон', icon: '🎯' },
  { id: 'erase', label: 'Ластик', icon: '🧽' },
  { id: 'restore', label: 'Восстановить', icon: '🖌️' },
  { id: 'text', label: 'Текст', icon: 'T' },
  { id: 'emoji', label: 'Эмодзи', icon: '✨' },
  { id: 'move', label: 'Перемещение', icon: '↔' },
];

const emojiOptions = ['🦈', '✨', '❤️', '🔥', '😂', '🎉'];

const StickerEditorModal = ({ sticker, isOpen, onClose, onSaved }: StickerEditorModalProps) => {
  const [text, setText] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textFontSize, setTextFontSize] = useState(36);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const editor = useStickerCanvas(isOpen ? `${API_BASE}${sticker.image_url}` : null);

  const restoreOpenerFocus = useCallback(() => {
    openerRef.current?.focus();
  }, []);

  const requestClose = useCallback(() => {
    if (saving) return;
    if (!editor.isDirty || window.confirm('Закрыть редактор без сохранения изменений?')) {
      restoreOpenerFocus();
      onClose();
    }
  }, [editor.isDirty, onClose, restoreOpenerFocus, saving]);

  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      requestClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isOpen, requestClose]);

  if (!isOpen) return null;

  const save = async () => {
    try {
      setSaving(true);
      const png = await editor.exportPng();
      const formData = new FormData();
      formData.append('file', new File([png], `sticker-${sticker.id}.png`, { type: 'image/png' }));
      await api.put(`/stickers/sticker/${sticker.id}/image`, formData);
      onSaved();
      restoreOpenerFocus();
      onClose();
    } catch (error) {
      logHttpError('Ошибка сохранения стикера', error);
      alert('Не удалось сохранить стикер. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const activeToolClass = (tool: StickerCanvasTool) => (
    editor.tool === tool
      ? 'bg-blue-600 text-white border-blue-400 shadow-lg shadow-blue-900/30'
      : 'bg-[#15171c] text-gray-300 border-gray-700 hover:border-blue-500 hover:text-white'
  );

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm"
      onClick={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Внутренний редактор стикера"
        onKeyDown={handleDialogKeyDown}
        className="flex max-h-[96vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-gray-800 bg-[#1a1d24] shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-gray-800 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-black text-white sm:text-2xl">Редактор стикера</h2>
            <p className="text-sm text-gray-400">Холст 512 × 512 с прозрачным PNG на выходе</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Закрыть редактор"
            onClick={requestClose}
            className="rounded-full p-2 text-xl text-gray-400 transition hover:bg-gray-800 hover:text-white"
          >
            ✕
          </button>
        </header>

        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 lg:grid-cols-[220px_minmax(0,1fr)_220px] lg:overflow-hidden sm:p-6">
          <aside aria-label="Инструменты редактора" className="space-y-3">
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
              {tools.map((tool) => (
                <button
                  key={tool.id}
                  type="button"
                  aria-pressed={editor.tool === tool.id}
                  onClick={() => editor.setTool(tool.id)}
                  className={`rounded-xl border px-3 py-2.5 text-left text-sm font-bold transition ${activeToolClass(tool.id)}`}
                >
                  <span className="mr-2" aria-hidden="true">{tool.icon}</span>{tool.label}
                </button>
              ))}
            </div>

            {editor.tool === 'background' && (
              <label className="block rounded-xl border border-gray-800 bg-[#0f1115] p-3 text-sm text-gray-300">
                <span className="mb-2 block font-bold text-white">Допуск цвета</span>
                <input
                  aria-label="Допуск цвета"
                  type="range"
                  min="0"
                  max="441"
                  value={editor.tolerance}
                  onChange={(event) => editor.setTolerance(Number(event.target.value))}
                  className="w-full accent-blue-500"
                />
                <span className="text-xs text-gray-500">{editor.tolerance}</span>
              </label>
            )}

            {editor.tool === 'text' && (
              <div className="rounded-xl border border-gray-800 bg-[#0f1115] p-3">
                <label htmlFor="sticker-editor-text" className="mb-2 block text-sm font-bold text-white">Текст слоя</label>
                <input
                  id="sticker-editor-text"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  className="mb-2 w-full rounded-lg border border-gray-700 bg-[#15171c] px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                />
                <div className="mb-2 grid grid-cols-[auto_1fr] items-center gap-2 text-sm text-gray-300">
                  <label htmlFor="sticker-editor-text-color">Цвет текста</label>
                  <input
                    id="sticker-editor-text-color"
                    aria-label="Цвет текста"
                    type="color"
                    value={textColor}
                    onChange={(event) => setTextColor(event.target.value)}
                    className="h-9 w-full rounded border border-gray-700 bg-[#15171c]"
                  />
                </div>
                <label className="mb-2 block text-sm text-gray-300">
                  <span className="mb-1 block">Размер текста: {textFontSize}px</span>
                  <input
                    aria-label="Размер текста"
                    type="range"
                    min="16"
                    max="96"
                    value={textFontSize}
                    onChange={(event) => setTextFontSize(Number(event.target.value))}
                    className="w-full accent-blue-500"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    editor.addLayer('text', text, { color: textColor, fontSize: textFontSize });
                    setText('');
                  }}
                  disabled={!text.trim()}
                  className="w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Добавить текст
                </button>
              </div>
            )}

            {editor.tool === 'emoji' && (
              <div className="grid grid-cols-3 gap-2 rounded-xl border border-gray-800 bg-[#0f1115] p-3">
                {emojiOptions.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    aria-label={`Добавить эмодзи ${emoji}`}
                    onClick={() => editor.addLayer('emoji', emoji)}
                    className="rounded-lg bg-[#15171c] p-2 text-xl transition hover:bg-gray-700"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            {editor.tool === 'move' && editor.selectedLayerId && (
              <label className="block rounded-xl border border-gray-800 bg-[#0f1115] p-3 text-sm text-gray-300">
                <span className="mb-2 block font-bold text-white">Масштаб слоя</span>
                <input
                  aria-label="Масштаб слоя"
                  type="range"
                  min="0.25"
                  max="3"
                  step="0.05"
                  value={editor.layers.find((layer) => layer.id === editor.selectedLayerId)?.scale ?? 1}
                  onChange={(event) => editor.setSelectedLayerScale(Number(event.target.value))}
                  className="w-full accent-blue-500"
                />
              </label>
            )}
          </aside>

          <div className="flex min-h-[320px] items-center justify-center overflow-auto rounded-2xl border border-gray-800 bg-[#0f1115] p-3">
            {editor.loadError ? (
              <p role="alert" className="text-center text-red-300">{editor.loadError}</p>
            ) : (
              <canvas
                ref={editor.canvasRef}
                width="512"
                height="512"
                aria-label="Холст редактора стикера"
                onPointerDown={editor.onPointerDown}
                onPointerMove={editor.onPointerMove}
                onPointerUp={editor.onPointerUp}
                onPointerCancel={editor.onPointerUp}
                className="aspect-square w-full max-w-[512px] touch-none rounded-xl bg-[linear-gradient(45deg,#1f2937_25%,transparent_25%),linear-gradient(-45deg,#1f2937_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#1f2937_75%),linear-gradient(-45deg,transparent_75%,#1f2937_75%)] bg-[length:24px_24px] bg-[position:0_0,0_12px,12px_-12px,-12px_0px] shadow-xl"
              />
            )}
          </div>

          <aside aria-label="Слои и история" className="space-y-3">
            <div className="rounded-xl border border-gray-800 bg-[#0f1115] p-3">
              <h3 className="mb-2 text-sm font-bold text-white">Слои</h3>
              {editor.layers.length === 0 ? (
                <p className="text-xs text-gray-500">Нет добавленных слоёв</p>
              ) : (
                <div className="space-y-1">
                  {editor.layers.map((layer) => (
                    <button
                      key={layer.id}
                      type="button"
                      aria-pressed={editor.selectedLayerId === layer.id}
                      onClick={() => editor.selectLayer(layer.id)}
                      className={`w-full rounded-lg px-2 py-1.5 text-left text-sm transition ${editor.selectedLayerId === layer.id ? 'bg-blue-600/30 text-white' : 'bg-[#15171c] text-gray-300 hover:bg-gray-800'}`}
                    >
                      {layer.value}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={editor.undo} disabled={!editor.canUndo} className="rounded-xl bg-gray-700 px-2 py-2 text-sm font-bold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-40">Отменить</button>
              <button type="button" onClick={editor.redo} disabled={!editor.canRedo} className="rounded-xl bg-gray-700 px-2 py-2 text-sm font-bold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-40">Повторить</button>
              <button type="button" onClick={editor.reset} disabled={!editor.isDirty} className="rounded-xl bg-red-800 px-2 py-2 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">Сбросить</button>
            </div>
            <p className="text-xs text-gray-500">Ctrl/Cmd + Z — отмена, Ctrl/Cmd + Y или Shift + Z — повтор.</p>
          </aside>
        </div>

        <footer className="flex flex-col-reverse gap-3 border-t border-gray-800 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button type="button" onClick={requestClose} disabled={saving} className="rounded-xl bg-gray-700 px-5 py-2.5 font-bold text-white transition hover:bg-gray-600 disabled:opacity-40">Отмена</button>
          <button type="button" onClick={() => void save()} disabled={!editor.isReady || saving} className="rounded-xl bg-blue-600 px-5 py-2.5 font-bold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40">{saving ? 'Сохранение…' : 'Сохранить'}</button>
        </footer>
      </section>
    </div>
  );
};

export default StickerEditorModal;
